// apps/admin/src/pages/monitoring/WebhookLogsPage.tsx
//
// Phase 8.3 Wave 7 Task 54 — Monitoring Webhook Logs page (admin).
//
// vg-binding: CONTEXT:decisions=P8.D-129,P8.D-140
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: PLAN:task-54
// vg-binding: G-20
// vg-binding: API-CONTRACTS:get-api-v1-admin-monitoring-webhook-logs
// vg-binding: API-CONTRACTS:post-api-v1-admin-monitoring-webhook-logs-id-retry
// vg-binding: API-CONTRACTS:get-api-v1-admin-monitoring-webhook-logs-id-payload
//
// Per CONTEXT.md (Webhook Logs surface) + (signature
// timestamp/ip/idempotency 4-axis validation). Route /monitoring/webhook-logs
// requires `monitoring:read` permission (enforced server-side; sidebar already
// gates visibility via groups.ts line 358).
//
// Surface:
//   - 4 stats cards (total_last_hour / failed / avg_response_time / retry_rate)
//   - Filter bar: source platform / status / date-from / date-to
//   - Table: timestamp | source | event_type | status | response_time |
//            retry_count | signature OK badges (4-axis mini) | actions
//   - Per-row actions: Retry (confirm) / View payload (modal — block)
//   - Cursor pagination (Next / Prev disabled — single-direction cursor API)
//   - URL state sync (source, status, date_from, date_to, cursor) per VG R7
//
// Endpoint consumption:
//   GET   /api/v1/admin/monitoring/webhook-logs?source=&status=&date_from=&date_to=&cursor=&limit=50
//   POST  /api/v1/admin/monitoring/webhook-logs/:id/retry  (idempotency-key)
//   GET   /api/v1/admin/monitoring/webhook-logs/:id/payload  (via modal)
//
// Envelope handling note: the canonical apiClient response interceptor
// unwraps `{ ok, data }` → `data` and discards `meta`. Stats + cursor_next
// live in `meta`, so the list call uses a small fetch wrapper that keeps
// the full envelope (`fetchWebhookLogs`). Mutations (retry) still use
// apiClient because they only need the `data` payload.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  cn,
  useToast,
  PageHeader,
} from '@printway/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';

import { apiClient, ApiError, API_BASE_URL } from '../../lib/apiClient.js';
import { WebhookPayloadModal } from '../../features/monitoring/WebhookPayloadModal.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { ensureP83Loaded } from '../../i18n/index.js';

// ---------------------------------------------------------------------------
// Wire shapes — mirror API-CONTRACTS GET /admin/monitoring/webhook-logs.
// ---------------------------------------------------------------------------

interface WebhookValidation {
  signature_ok: boolean;
  timestamp_ok: boolean;
  ip_ok: boolean;
  idempotency_ok: boolean;
}

interface WebhookLogRow {
  id: string;
  timestamp: string;
  source: string;
  event_type: string;
  status: string;
  response_time_ms: number;
  retry_count: number;
  validation: WebhookValidation;
}

interface WebhookLogsStats {
  total_last_hour: number;
  failed: number;
  avg_response_time_ms: number;
  retry_rate: number;
}

interface WebhookLogsMeta {
  cursor_next: string | null;
  stats: WebhookLogsStats;
}

interface WebhookLogsEnvelope {
  ok: boolean;
  data?: WebhookLogRow[];
  meta?: WebhookLogsMeta;
  error?: {
    code: string;
    message: string;
    user_message?: string;
  };
}

interface WebhookRetryData {
  id: string;
  enqueued_at: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_LIMIT = 50;

// Radix Select forbids SelectItem value="" — sentinel for the "all" option.
const ALL_VALUE = '__all__';

// Brand source values keep their literal display labels; the "all" option +
// status labels resolve at render time via i18n (rule: enum display labels).
const SOURCE_OPTIONS: Array<{ value: string; label?: string; labelKey?: string }> = [
  { value: '', labelKey: 'monitoring.webhookLogs.filters.allSources' },
  { value: 'Shopify', labelKey: 'monitoring.webhookLogs.sources.shopify' },
  { value: 'Etsy', labelKey: 'monitoring.webhookLogs.sources.etsy' },
  { value: 'WooCommerce', labelKey: 'monitoring.webhookLogs.sources.woocommerce' },
  { value: 'TikTok', labelKey: 'monitoring.webhookLogs.sources.tiktok' },
  { value: 'Amazon', labelKey: 'monitoring.webhookLogs.sources.amazon' },
];

const STATUS_OPTIONS: Array<{ value: string; labelKey: string }> = [
  { value: '', labelKey: 'monitoring.webhookLogs.filters.allStatuses' },
  { value: 'success', labelKey: 'monitoring.webhookLogs.status.success' },
  { value: 'failed', labelKey: 'monitoring.webhookLogs.status.failed' },
  { value: 'retry', labelKey: 'monitoring.webhookLogs.status.retry' },
];

type Translator = (key: string, vars?: Record<string, unknown>) => string;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildAbsoluteUrl(path: string, query: URLSearchParams): string {
  const base = API_BASE_URL || '';
  const sep = base.endsWith('/') ? '' : '';
  const qs = query.toString();
  return `${base}${sep}${path}${qs ? `?${qs}` : ''}`;
}

function readCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(/;\s*/);
  for (const c of cookies) {
    const [k, ...rest] = c.split('=');
    if (k === 'XSRF-TOKEN') return decodeURIComponent(rest.join('='));
  }
  return null;
}

/**
 * Direct-fetch wrapper that preserves the full envelope (data + meta).
 * Required because apiClient's response interceptor unwraps to `data`
 * only, losing `meta.stats` and `meta.cursor_next`.
 *
 * INTERFACE-STANDARDS §error-shape: error envelope priority
 * `error.user_message > error.message > network_fallback` is enforced
 * here so the caller can surface a single human-readable message.
 */
async function fetchWebhookLogs(
  params: Record<string, string>,
  messages: { loadFail: string; network: string },
  signal?: AbortSignal,
): Promise<{ rows: WebhookLogRow[]; meta: WebhookLogsMeta | null }> {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v) query.set(k, v);
  });
  const url = buildAbsoluteUrl('/api/v1/admin/monitoring/webhook-logs', query);

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id':
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  };
  const csrf = readCsrfToken();
  if (csrf) headers['X-XSRF-TOKEN'] = csrf;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers,
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    throw new Error(messages.network, { cause: err });
  }

  let body: WebhookLogsEnvelope | null;
  try {
    body = (await res.json()) as WebhookLogsEnvelope;
  } catch {
    body = null;
  }

  if (!res.ok || !body || body.ok === false) {
    // INTERFACE-STANDARDS message priority.
    const msg =
      body?.error?.user_message ??
      body?.error?.message ??
      messages.loadFail;
    throw new Error(msg);
  }

  return {
    rows: body.data ?? [],
    meta: body.meta ?? null,
  };
}

function relativeTime(iso: string, t: Translator): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const sec = Math.floor((Date.now() - then) / 1000);
  if (sec < 60) return t('monitoring.common.secondsAgo', { count: sec });
  const min = Math.floor(sec / 60);
  if (min < 60) return t('monitoring.common.minutesAgo', { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t('monitoring.common.hoursAgo', { count: hr });
  const days = Math.floor(hr / 24);
  if (days < 30) return t('monitoring.common.daysAgo', { count: days });
  return new Date(iso).toLocaleDateString();
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'success':
      return 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
    case 'failed':
      return 'bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800';
    case 'retry':
      return 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800';
    default:
      return 'bg-pw-surface text-pw-text border-pw-border';
  }
}

function statusLabel(status: string, t: Translator): string {
  switch (status) {
    case 'success':
      return t('monitoring.webhookLogs.status.success');
    case 'failed':
      return t('monitoring.webhookLogs.status.failed');
    case 'retry':
      return t('monitoring.webhookLogs.status.retry');
    default:
      return status || '—';
  }
}

function newIdempotencyKey(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `idemp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function truncate(s: string, n: number): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

function ValidationDot({
  ok,
  label,
  t,
}: {
  ok: boolean;
  label: string;
  t: Translator;
}): JSX.Element {
  const result = ok
    ? t('monitoring.webhookLogs.validation.pass')
    : t('monitoring.webhookLogs.validation.fail');
  return (
    <span
      className={cn(
        'inline-block h-2 w-2 rounded-full',
        ok ? 'bg-emerald-500' : 'bg-red-500',
      )}
      title={`${label}: ${result}`}
      aria-label={`${label}: ${result}`}
    />
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function WebhookLogsPage(): JSX.Element {
  const { toast } = useToast();
  const { t, locale } = useTranslation('p83');
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    void ensureP83Loaded(locale);
  }, [locale]);

  const [rows, setRows] = useState<WebhookLogRow[]>([]);
  const [stats, setStats] = useState<WebhookLogsStats | null>(null);
  const [cursorNext, setCursorNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [retryTarget, setRetryTarget] = useState<WebhookLogRow | null>(null);
  const [payloadTarget, setPayloadTarget] = useState<WebhookLogRow | null>(null);

  // Track cursor history so user can step backwards through pages.
  const cursorHistoryRef = useRef<Array<string | null>>([null]);

  // ---- URL state ----
  const filterSource = searchParams.get('source') ?? '';
  const filterStatus = searchParams.get('status') ?? '';
  const filterDateFrom = searchParams.get('date_from') ?? '';
  const filterDateTo = searchParams.get('date_to') ?? '';
  const cursor = searchParams.get('cursor') ?? '';

  function updateParam(key: string, value: string): void {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    // Resetting any filter clears the cursor so user starts from page 1.
    if (key !== 'cursor') {
      next.delete('cursor');
      cursorHistoryRef.current = [null];
    }
    setSearchParams(next, { replace: true });
  }

  // ---- Fetch ----
  const listParams = useMemo(() => {
    const p: Record<string, string> = { limit: String(PAGE_LIMIT) };
    if (filterSource) p.source = filterSource;
    if (filterStatus) p.status = filterStatus;
    if (filterDateFrom) p.date_from = filterDateFrom;
    if (filterDateTo) p.date_to = filterDateTo;
    if (cursor) p.cursor = cursor;
    return p;
  }, [filterSource, filterStatus, filterDateFrom, filterDateTo, cursor]);

  const fetchRows = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      setLoading(true);
      try {
        const { rows: newRows, meta } = await fetchWebhookLogs(
          listParams,
          {
            loadFail: t('monitoring.webhookLogs.toast.loadFailedFallback'),
            network: t('monitoring.webhookLogs.toast.networkError'),
          },
          signal,
        );
        setRows(newRows);
        setStats(meta?.stats ?? null);
        setCursorNext(meta?.cursor_next ?? null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        const msg =
          err instanceof Error
            ? err.message
            : t('monitoring.webhookLogs.toast.loadFailedDataFallback');
        toast({
          title: t('monitoring.webhookLogs.toast.loadFailedTitle'),
          description: msg,
          variant: 'error',
        });
        setRows([]);
        setStats(null);
        setCursorNext(null);
      } finally {
        setLoading(false);
      }
    },
    [listParams, toast, t],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    void fetchRows(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchRows, refreshTick]);

  // ---- Retry handler ----
  async function handleRetry(row: WebhookLogRow): Promise<void> {
    setSubmitting(true);
    try {
      await apiClient.post<WebhookRetryData>(
        `/api/v1/admin/monitoring/webhook-logs/${encodeURIComponent(row.id)}/retry`,
        {},
        { idempotencyKey: newIdempotencyKey() },
      );
      toast({
        title: t('monitoring.webhookLogs.toast.enqueuedTitle'),
        description: t('monitoring.webhookLogs.toast.enqueuedDesc', {
          id: truncate(row.id, 12),
        }),
        variant: 'success',
      });
    } catch (err) {
      // INTERFACE-STANDARDS message priority — ApiError.message already
      // resolves user_message > message.
      if (err instanceof ApiError && err.code === 'NOT_RETRYABLE') {
        toast({
          title: t('monitoring.webhookLogs.toast.cannotRetryTitle'),
          description: err.message,
          variant: 'warning',
        });
      } else {
        const msg =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : t('monitoring.webhookLogs.toast.retryFailedFallback');
        toast({
          title: t('monitoring.webhookLogs.toast.retryFailedTitle'),
          description: msg,
          variant: 'error',
        });
      }
    } finally {
      setSubmitting(false);
      setRetryTarget(null);
      setRefreshTick((n) => n + 1);
    }
  }

  // ---- Pagination ----
  function goNext(): void {
    if (!cursorNext) return;
    cursorHistoryRef.current.push(cursor || null);
    updateParam('cursor', cursorNext);
  }

  function goPrev(): void {
    const hist = cursorHistoryRef.current;
    if (hist.length <= 1) return;
    hist.pop();
    const prev = hist[hist.length - 1] ?? null;
    updateParam('cursor', prev ?? '');
  }

  const canGoPrev = cursorHistoryRef.current.length > 1;
  const canGoNext = Boolean(cursorNext);

  // ---- Render ----
  return (
    <div className="space-y-4 p-6" data-testid="webhook-logs-page">
      {/* Header */}
      <PageHeader
        title={t('monitoring.webhookLogs.title')}
        subtitle={t('monitoring.webhookLogs.description')}
        actions={
          <Button
            variant="ghost"
            onClick={() => setRefreshTick((n) => n + 1)}
            disabled={loading}
            data-testid="webhook-logs-refresh"
          >
            {loading
              ? t('monitoring.webhookLogs.loading')
              : t('monitoring.webhookLogs.refresh')}
          </Button>
        }
      />

      {/* Stats cards */}
      <section
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="webhook-logs-stats"
      >
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-pw-text-muted">
              {t('monitoring.webhookLogs.stats.totalLastHour')}
            </div>
            <div
              className="mt-1 text-2xl font-semibold text-pw-text"
              data-testid="stat-total"
            >
              {stats ? stats.total_last_hour.toLocaleString() : '—'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-pw-text-muted">
              {t('monitoring.webhookLogs.stats.failed')}
            </div>
            <div
              className={cn(
                'mt-1 text-2xl font-semibold',
                stats && stats.failed > 0 ? 'text-red-600 dark:text-red-300' : 'text-pw-text',
              )}
              data-testid="stat-failed"
            >
              {stats ? stats.failed.toLocaleString() : '—'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-pw-text-muted">
              {t('monitoring.webhookLogs.stats.avgResponseTime')}
            </div>
            <div
              className="mt-1 text-2xl font-semibold text-pw-text"
              data-testid="stat-avg-rt"
            >
              {stats ? `${Math.round(stats.avg_response_time_ms)} ms` : '—'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-pw-text-muted">
              {t('monitoring.webhookLogs.stats.retryRate')}
            </div>
            <div
              className="mt-1 text-2xl font-semibold text-pw-text"
              data-testid="stat-retry-rate"
            >
              {stats ? `${(stats.retry_rate * 100).toFixed(1)}%` : '—'}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap gap-3 p-4">
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-pw-text-muted">
              {t('monitoring.webhookLogs.filters.source')}
            </label>
            <Select
              value={filterSource === '' ? ALL_VALUE : filterSource}
              onValueChange={(value) =>
                updateParam('source', value === ALL_VALUE ? '' : value)
              }
            >
              <SelectTrigger data-testid="filter-source">
                <SelectValue
                  placeholder={t('monitoring.webhookLogs.filters.allSources')}
                />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value || 'all'} value={opt.value || ALL_VALUE}>
                    {opt.labelKey ? t(opt.labelKey) : opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-pw-text-muted">
              {t('monitoring.webhookLogs.filters.status')}
            </label>
            <Select
              value={filterStatus === '' ? ALL_VALUE : filterStatus}
              onValueChange={(value) =>
                updateParam('status', value === ALL_VALUE ? '' : value)
              }
            >
              <SelectTrigger data-testid="filter-status">
                <SelectValue
                  placeholder={t('monitoring.webhookLogs.filters.allStatuses')}
                />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value || 'all'} value={opt.value || ALL_VALUE}>
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-pw-text-muted">
              {t('monitoring.webhookLogs.filters.dateFrom')}
            </label>
            <Input
              type="date"
              value={filterDateFrom ? filterDateFrom.slice(0, 10) : ''}
              onChange={(e) => {
                const v = e.target.value;
                updateParam(
                  'date_from',
                  v ? new Date(`${v}T00:00:00.000Z`).toISOString() : '',
                );
              }}
              data-testid="filter-date-from"
            />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-pw-text-muted">
              {t('monitoring.webhookLogs.filters.dateTo')}
            </label>
            <Input
              type="date"
              value={filterDateTo ? filterDateTo.slice(0, 10) : ''}
              onChange={(e) => {
                const v = e.target.value;
                updateParam(
                  'date_to',
                  v ? new Date(`${v}T23:59:59.999Z`).toISOString() : '',
                );
              }}
              data-testid="filter-date-to"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-pw-border bg-pw-bg-muted text-left text-xs uppercase text-pw-text-muted">
                <tr>
                  <th className="px-3 py-2">{t('monitoring.webhookLogs.table.ts')}</th>
                  <th className="px-3 py-2">{t('monitoring.webhookLogs.table.source')}</th>
                  <th className="px-3 py-2">{t('monitoring.webhookLogs.table.event')}</th>
                  <th className="px-3 py-2">{t('monitoring.webhookLogs.table.status')}</th>
                  <th className="px-3 py-2">{t('monitoring.webhookLogs.table.validate')}</th>
                  <th className="px-3 py-2">{t('monitoring.webhookLogs.table.rt')}</th>
                  <th className="px-3 py-2">{t('monitoring.webhookLogs.table.retry')}</th>
                  <th className="w-44 px-3 py-2 text-right">
                    {t('monitoring.webhookLogs.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-3 py-12 text-center text-pw-text-muted"
                      data-testid="webhook-logs-empty"
                    >
                      {t('monitoring.webhookLogs.table.empty')}
                    </td>
                  </tr>
                ) : null}
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-pw-border last:border-b-0 hover:bg-pw-bg-muted/40"
                    data-testid={`webhook-row-${row.id}`}
                  >
                    <td
                      className="px-3 py-2 text-pw-text-muted"
                      title={new Date(row.timestamp).toLocaleString()}
                    >
                      {relativeTime(row.timestamp, t)}
                    </td>
                    <td className="px-3 py-2 font-medium text-pw-text">
                      {row.source}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-pw-text-muted">
                      {row.event_type}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={cn(
                          'inline-block rounded-full border px-2 py-0.5 text-xs',
                          statusBadgeClass(row.status),
                        )}
                      >
                        {statusLabel(row.status, t)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div
                        className="flex items-center gap-1"
                        data-testid={`webhook-validation-${row.id}`}
                      >
                        <ValidationDot
                          ok={row.validation.signature_ok}
                          label={t('monitoring.webhookLogs.validation.signature')}
                          t={t}
                        />
                        <ValidationDot
                          ok={row.validation.timestamp_ok}
                          label={t('monitoring.webhookLogs.validation.timestamp')}
                          t={t}
                        />
                        <ValidationDot
                          ok={row.validation.ip_ok}
                          label={t('monitoring.webhookLogs.validation.ip')}
                          t={t}
                        />
                        <ValidationDot
                          ok={row.validation.idempotency_ok}
                          label={t('monitoring.webhookLogs.validation.idempotency')}
                          t={t}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-pw-text-muted">
                      {row.response_time_ms}
                    </td>
                    <td className="px-3 py-2">{row.retry_count}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPayloadTarget(row)}
                          data-testid={`webhook-payload-${row.id}`}
                        >
                          {t('monitoring.webhookLogs.rowActions.payload')}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setRetryTarget(row)}
                          disabled={submitting || row.status === 'success'}
                          data-testid={`webhook-retry-${row.id}`}
                        >
                          {t('monitoring.webhookLogs.rowActions.retry')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-pw-text-muted">
        <span>
          {t('monitoring.webhookLogs.pagination.rows', { count: rows.length })}
        </span>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={goPrev}
            disabled={!canGoPrev || loading}
            data-testid="webhook-page-prev"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            <span>{t('monitoring.webhookLogs.pagination.prev')}</span>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={goNext}
            disabled={!canGoNext || loading}
            data-testid="webhook-page-next"
          >
            <span>{t('monitoring.webhookLogs.pagination.next')}</span>
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {/* Confirm retry */}
      {retryTarget ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setRetryTarget(null)}
          title={t('monitoring.webhookLogs.retryDialog.title')}
          description={t('monitoring.webhookLogs.retryDialog.description', {
            event: truncate(retryTarget.event_type, 32),
            source: retryTarget.source,
          })}
          confirmLabel={t('monitoring.webhookLogs.retryDialog.confirm')}
          type="neutral"
          loading={submitting}
          onConfirm={() => void handleRetry(retryTarget)}
        />
      ) : null}

      {/* Payload modal */}
      <WebhookPayloadModal
        open={payloadTarget !== null}
        webhookId={payloadTarget?.id ?? null}
        onOpenChange={(open) => {
          if (!open) setPayloadTarget(null);
        }}
      />
    </div>
  );
}
